import { Flex, IconButton, Text, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { showErrorToast, translateSubElementString } from "../../../../../../../utils";
import {
  useGetTreatmentOfSoldProducts
} from "../../../../../../../hooks/query/reports";
import { hideAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import CategoryPreviewWrapper from "./CategoryPreviewWrapper";
import { NumberField } from "../../../../../../../components/NumberField";
import { DataTable } from "../../../../../../../components/DataTable";
import { ReviewDataModal } from "../ReviewDataModal";
import { useAuth } from "../../../../../../../context";
import { ReportsApi } from "../../../../../../../api";
import { fetchAllPages, plainExportHeader } from "../../../../../../../utils/fetchAllPages";

const numberOrNull = (value) => (value === null || value === undefined || value === "" ? null : Number(value));

function TreatmentOfSoldProductsPreview({ reportId, currentElement }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.wasteGeneratedInOperations"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const questionnaireKeyPrefix = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations";
  const { t: questionnaireTranslation } = useTranslation("translation", {
    keyPrefix: questionnaireKeyPrefix
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose
  } = useDisclosure();

  const [dataList, setDataList] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    // Items are already pre-sorted by the backend by "wasteActivity", "wasteType" and "disposalMethod"
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0
  });

  const {
    totalPages,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(() => {
      const handleReviewData = (attribute) => {
        onReviewModalOpen();
        setSelectedAttribute(attribute);
        if (attribute) {
          setReviewData(currentElement[`${attribute}Items`].map(el => {
            return {
              ...el,
              [attribute]: questionnaireTranslation(`${attribute}.options.${el[attribute]}`,
                { defaultValue: el[attribute] ?? "" })
            };
          }));
        }
      };

      return [
        {
          header: <Flex alignItems="center">
            {t("wasteActivity")}
            <Tooltip
              label={t("reviewData", { attribute: t("wasteActivity") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="wasteActivityInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("wasteActivity");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "wasteActivity",
          meta: { exportHeader: t("wasteActivity") },
          size: 150
        },
        {
          header: t("wasteType"),
          accessorKey: "wasteType",
          size: 180,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: <Flex alignItems="center">
            {t("disposalMethod")}
            <Tooltip
              label={t("reviewData", { attribute: t("disposalMethod") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="disposalMethodInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("disposalMethod");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "disposalMethod",
          meta: { exportHeader: t("disposalMethod") },
          size: 170
        },
        {
          header: t("weight"),
          accessorKey: "weight",
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: translateSubElementString("reportsPage.scopeThree.inputs.wasteGeneratedInOperations.emissions"),
          accessorKey: "emissions",
          meta: { exportHeader: plainExportHeader(globalTranslation("reportsPage.scopeThree.inputs.wasteGeneratedInOperations.emissions")) },
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        }
      ];
    },
    [t, globalTranslation, onReviewModalOpen, questionnaireTranslation, currentElement]
  );

  const handleReviewModalClose = () => {
    setSelectedAttribute(null);
    setReviewData(null);
    onReviewModalClose();
  };

  // One mapping for the page on screen and for Export; the export keeps the numbers numeric so Excel can sum them.
  const toRow = useCallback(
    (item, { forExport = false } = {}) => ({
      id: item.id,
      wasteActivity: questionnaireTranslation(`wasteActivity.options.${item.wasteActivity}`,
        { defaultValue: item.wasteActivity ?? "" }),
      wasteType: questionnaireTranslation(`wasteType.options.${item.wasteActivity}.${item.wasteType}`,
        { defaultValue: item.wasteType ?? "" }),
      disposalMethod: questionnaireTranslation(`disposalMethod.options.${item.disposalMethod}`,
        { defaultValue: item.disposalMethod ?? "" }),
      weight: forExport ? numberOrNull(item.weight) : Number(item.weight)?.toLocaleString("en-GB", { maximumFractionDigits: 4 }),
      emissions: forExport ? numberOrNull(item.co2Emissions) : Number(item.co2Emissions)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })
    }),
    [questionnaireTranslation]
  );

  const { callApi } = useAuth();
  // Every row, not the page on screen, in the same order the table shows.
  const fetchAllRowsForExport = () =>
    fetchAllPages(({ page, size }) =>
      callApi(ReportsApi.getTreatmentOfSoldProducts, { reportId, params: { page, size, sort: queryPageSortBy } })
    ).then((items) => items.map((item) => toRow(item, { forExport: true })));

  const {
    isLoading,
    isFetching
  } = useGetTreatmentOfSoldProducts(
    reportId,
    { page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const dataItems = pageContent.map((item) => toRow(item));

        setDataList(dataItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.message),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0
    }
  );

  return (
    <CategoryPreviewWrapper>
      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            "reportsPage.scopeThree.inputs.mainTotalEmissions"
          )}
        </Text>
        <NumberField value={currentElement?.totalCo2Emissions} marginStart="2" />
      </Flex>
      <DataTable
        data={dataList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight={dataList && dataList.length > 0 ? "370px" : null}
        marginBlockEnd={8}
        serverSidePagination={true}
        fetchAllRowsForExport={fetchAllRowsForExport}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      {
        isReviewModalOpen && selectedAttribute !== null && reviewData !== null && (
          <ReviewDataModal
            isOpen={isReviewModalOpen}
            onClose={handleReviewModalClose}
            dataAttribute={selectedAttribute}
            gridHeader={t(selectedAttribute)}
            data={reviewData}
          />
        )
      }
    </CategoryPreviewWrapper>
  );
}

export default TreatmentOfSoldProductsPreview;